import {
  Adjuntos,
  Alerta,
  Badge,
  BuscadorDireccion,
  Button,
  Card,
  Casilla,
  DataList,
  Dialog,
  MapaDireccion,
  MetricCard,
  MontoField,
  Row,
  Select,
  SelectorArchivos,
  Spinner,
  Stack,
  TextField,
} from "@/components";
import type { ReactNode } from "react";
import type { Adjunto, CompraTerreno, ItemCompra, ItemPrevisto, Terreno } from "@/lib/api";
import { ACCEPT_ADJUNTO } from "@/lib/adjuntos";
import { formatFecha, formatFechaLocal, formatMoneda, formatNumero } from "@/lib/format";
import { etiquetaFee } from "@/lib/gastos";
import {
  TEXTOS_ITEM,
  conceptoItem,
  direccionParaMapa,
  puedeConfirmarCompra,
  requisitosCompra,
  textoEntreCalles,
  textoHonorarios,
  textoLocalidad,
  textoPrevistosAlConfirmar,
  tieneUbicacion,
  tituloDireccion,
  type CampoDireccion,
} from "@/lib/terreno";
import { useTerrenoTab } from "./useTerrenoTab";

type Ficha = ReturnType<typeof useTerrenoTab>;

const montoItem = (i: Pick<ItemCompra, "monto" | "moneda" | "monto_usd">) => (i.moneda === "USD" ? formatMoneda(i.monto, "USD") : `${formatMoneda(i.monto, i.moneda)} (${formatMoneda(i.monto_usd, "USD")})`);

/** Un dato chico de una tarjeta (va dentro de un `<dl>`). */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-chico text-texto-suave">{etiqueta}</dt>
      <dd className="font-medium tabular-nums">{valor}</dd>
    </div>
  );
}

function TituloTarjeta({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2 id={id} className="text-titulo font-light tracking-tight">
      {children}
    </h2>
  );
}

// ------------------------------------------------------------------ ubicación

function TarjetaUbicacion({ t, terreno }: { t: Ficha; terreno: Terreno }) {
  const conDatos = tieneUbicacion(terreno);
  const titulo = tituloDireccion(terreno);
  const entre = textoEntreCalles(terreno);
  const localidad = textoLocalidad(terreno);
  const mapa = direccionParaMapa(terreno);
  return (
    <Card role="region" aria-labelledby="titulo-ubicacion">
      <Stack espacio={4}>
        <Row justificar="entre" alinear="centro" espacio={3}>
          <TituloTarjeta id="titulo-ubicacion">Ubicación</TituloTarjeta>
          {t.esAdmin && conDatos && (
            <Button variante="secundario" tamanio="sm" aria-label="Editar ubicación" onClick={t.abrirUbicacion}>
              Editar
            </Button>
          )}
        </Row>
        {!conDatos ? (
          <Stack espacio={3}>
            <p className="text-texto-suave">Todavía no se cargó la ubicación del terreno.</p>
            {t.esAdmin && (
              <div>
                <Button onClick={t.abrirUbicacion}>Cargar ubicación</Button>
              </div>
            )}
          </Stack>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,18rem)] sm:items-start">
            <Stack espacio={3}>
              <div className="flex flex-col gap-0.5">
                <p className="text-subtitulo font-semibold leading-snug">{titulo ?? "Sin dirección"}</p>
                {entre && <p className="text-texto-suave">{entre}</p>}
                {localidad && <p className="text-chico text-texto-suave">{localidad}</p>}
              </div>
              <dl className="grid grid-cols-2 gap-3 rounded-control bg-superficie-2 p-3">
                <Dato etiqueta="Superficie" valor={terreno.superficie_m2 ? `${formatNumero(terreno.superficie_m2)} m²` : "Sin cargar"} />
                <Dato etiqueta="Zonificación" valor={terreno.zonificacion ?? "Sin cargar"} />
              </dl>
            </Stack>
            {mapa && <MapaDireccion direccion={mapa} latitud={terreno.latitud} longitud={terreno.longitud} miniatura />}
          </div>
        )}
      </Stack>
    </Card>
  );
}

function DialogoUbicacion({ t }: { t: Ficha }) {
  const campo = (clave: CampoDireccion | "superficie_m2" | "zonificacion") => ({
    value: t.form?.[clave] ?? "",
    onChange: (e: { target: { value: string } }) => t.cambiar(clave, e.target.value),
    error: t.errores[clave] ?? null,
  });
  return (
    <Dialog abierto={t.ubicacionAbierta} onCambio={(abierto) => !abierto && t.cerrarUbicacion()} titulo="Ubicación del terreno" ancho="amplio">
      {t.form && (
        <form onSubmit={(e) => void t.guardar(e)} noValidate>
          <Stack espacio={4}>
            {t.mostrarBuscador && (
              <Stack espacio={1}>
                <BuscadorDireccion buscar={t.buscarDirecciones} onElegir={(s) => void t.elegirDireccion(s)} onFalla={t.fallarBuscador} deshabilitado={t.cargandoDetalle} />
                <p className="text-chico text-texto-suave">Elegí una sugerencia y se completan los campos. Si no aparece, cargala a mano.</p>
              </Stack>
            )}
            <Alerta texto={t.avisoBuscador} tono="alerta" />
            {t.sinBuscador && <p className="text-chico text-texto-suave">El buscador de Google Maps no está configurado: cargá la dirección a mano.</p>}
            {t.cargandoDetalle && (
              <div className="text-primario">
                <Spinner tamanio="sm" etiqueta="Completando la dirección…" />
              </div>
            )}
            {t.form.google_place_id && <p className="text-chico text-exito">Con la ubicación exacta de Google Maps.</p>}
            <div className="grid grid-cols-[minmax(0,1fr)_6.5rem] gap-3">
              <TextField etiqueta="Calle" autoComplete="off" {...campo("calle")} />
              <TextField etiqueta="Altura" inputMode="numeric" autoComplete="off" {...campo("altura")} />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField etiqueta="Entre calle" autoComplete="off" {...campo("entre_calle_1")} />
              <TextField etiqueta="Y calle" autoComplete="off" {...campo("entre_calle_2")} />
              <TextField etiqueta="Localidad" autoComplete="off" {...campo("localidad")} />
              <TextField etiqueta="Partido" autoComplete="off" {...campo("partido")} />
              <TextField etiqueta="Provincia" autoComplete="off" {...campo("provincia")} />
              <TextField etiqueta="Código postal" autoComplete="off" {...campo("codigo_postal")} />
              <TextField etiqueta="Superficie del lote (m²)" inputMode="decimal" {...campo("superficie_m2")} />
              <TextField etiqueta="Zonificación" placeholder="Ej.: R2b" {...campo("zonificacion")} />
            </div>
            <Alerta texto={t.errorGuardar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrarUbicacion}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardando}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      )}
    </Dialog>
  );
}

// ------------------------------------------------------------------ compra abierta

function ListaItems({ t, etiqueta, items, vacio, conHonorarios = true }: { t: Ficha; etiqueta: string; items: ItemCompra[]; vacio: string; conHonorarios?: boolean }) {
  return (
    <DataList
      etiqueta={etiqueta}
      filas={items}
      claveFila={(i) => i.gasto_id}
      vacio={vacio}
      columnas={[
        { clave: "fecha", titulo: "Fecha", render: (i) => formatFecha(i.fecha) },
        { clave: "concepto", titulo: "Concepto", render: conceptoItem },
        { clave: "monto", titulo: "Monto", render: montoItem },
        ...(conHonorarios ? [{ clave: "honorarios", titulo: "Honorarios", render: (i: ItemCompra) => textoHonorarios(i) }] : []),
        {
          clave: "archivos",
          titulo: "Archivo",
          render: (i) =>
            i.adjuntos.length === 0 ? (
              <span className="text-texto-suave">Sin archivo</span>
            ) : (
              <Stack espacio={1}>
                {i.adjuntos.map((a: Adjunto) => (
                  <a key={a.id} href={t.rutaDescarga(a)} className="text-primario underline">
                    {a.nombre}
                  </a>
                ))}
              </Stack>
            ),
        },
      ]}
      acciones={
        t.esAdmin
          ? (i) => (
              <Button variante="secundario" tamanio="sm" aria-label={`Borrar ${i.concepto}`} onClick={() => t.pedirBorrar(i)}>
                Borrar
              </Button>
            )
          : undefined
      }
    />
  );
}

function ListaPrevistos({ t, items }: { t: Ficha; items: ItemPrevisto[] }) {
  return (
    <DataList
      etiqueta="Gastos previstos"
      filas={items}
      claveFila={(p) => p.id}
      vacio="Todavía no hay gastos de la compra."
      columnas={[
        { clave: "concepto", titulo: "Concepto", render: (p) => p.concepto },
        { clave: "monto", titulo: "Monto previsto", render: montoItem },
        { clave: "honorarios", titulo: "Honorarios", render: (p) => textoHonorarios(p) },
        {
          clave: "archivos",
          titulo: "Archivo",
          render: (p) =>
            p.adjuntos.length === 0 ? (
              <span className="text-texto-suave">Sin archivo</span>
            ) : (
              <Stack espacio={1}>
                {p.adjuntos.map((a: Adjunto) => (
                  <a key={a.id} href={t.rutaDescarga(a)} className="text-primario underline">
                    {a.nombre}
                  </a>
                ))}
              </Stack>
            ),
        },
      ]}
      acciones={
        t.esAdmin
          ? (p) => (
              <Row espacio={1}>
                <Button variante="secundario" tamanio="sm" aria-label={`Editar ${p.concepto}`} onClick={() => t.editarPrevisto(p)}>
                  Editar
                </Button>
                <Button variante="secundario" tamanio="sm" aria-label={`Borrar ${p.concepto}`} onClick={() => t.pedirBorrarPrevisto(p)}>
                  Borrar
                </Button>
              </Row>
            )
          : undefined
      }
    />
  );
}

function ParaConfirmar({ t, terreno }: { t: Ficha; terreno: Terreno }) {
  const requisitos = requisitosCompra(terreno);
  const puede = puedeConfirmarCompra(terreno);
  return (
    <section aria-labelledby="titulo-para-confirmar" className="rounded-control border border-borde bg-superficie-2 p-4">
      <Stack espacio={3}>
        <h3 id="titulo-para-confirmar" className="font-semibold">
          Para confirmar la compra
        </h3>
        {requisitos.length > 0 && (
          <ul aria-label="Para confirmar la compra" className="flex flex-col gap-2">
            {requisitos.map((r) => (
              <li key={r.texto} className="flex items-start gap-2">
                <span aria-hidden className={r.cumplido ? "font-semibold text-exito" : "text-texto-suave"}>
                  {r.cumplido ? "✓" : "○"}
                </span>
                <span className={r.cumplido ? undefined : "text-texto-suave"}>
                  <span className="sr-only">{r.cumplido ? "Listo: " : "Falta: "}</span>
                  {r.texto}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-chico text-texto-suave">Al confirmar se elige el scan de la escritura y la compra queda cerrada: ya no se cambian precio, fecha ni pagos.</p>
        {t.esAdmin && (
          <div>
            <Button onClick={t.abrirConfirmar} disabled={!puede}>
              Confirmar compra
            </Button>
          </div>
        )}
      </Stack>
    </section>
  );
}

function CompraAbierta({ t, terreno, compra }: { t: Ficha; terreno: Terreno; compra: CompraTerreno }) {
  return (
    <Card role="region" aria-labelledby="titulo-compra">
      <Stack espacio={4}>
        <Row justificar="entre" envolver espacio={3}>
          <TituloTarjeta id="titulo-compra">Compra</TituloTarjeta>
          {t.esAdmin && (
            <Button variante={compra.precio_acordado_usd ? "secundario" : "primario"} onClick={t.abrirPrecio}>
              {compra.precio_acordado_usd ? "Editar precio y escritura" : "Cargar compra"}
            </Button>
          )}
        </Row>
        {compra.precio_acordado_usd === null ? (
          <p className="text-chico text-texto-suave">Todavía no se cargó el precio acordado.</p>
        ) : (
          <>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Dato etiqueta="Precio acordado" valor={formatMoneda(compra.precio_acordado_usd, "USD")} />
              <Dato etiqueta="Fecha de escritura" valor={terreno.fecha_escritura ? formatFecha(terreno.fecha_escritura) : "Sin fecha todavía"} />
            </dl>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <MetricCard titulo="Pagado a cuenta" valor={formatMoneda(compra.pagado_usd, "USD")} />
              <MetricCard titulo="Saldo a pagar en la escritura" valor={formatMoneda(compra.saldo_escritura_usd ?? "0", "USD")} />
              <MetricCard titulo="Costo total del terreno" valor={formatMoneda(compra.total_usd ?? "0", "USD")} detalle="Precio acordado + gastos de la compra (previstos incluidos)" />
            </div>
          </>
        )}
        <Alerta texto={t.avisoArchivo} />
        {compra.precio_acordado_usd !== null && (
          <Stack espacio={3}>
            <Row justificar="entre" envolver espacio={3}>
              <h3 className="font-semibold">Pagos a cuenta</h3>
              {t.esAdmin && (
                <Button variante="secundario" tamanio="sm" onClick={() => t.abrirItem("pago")}>
                  {TEXTOS_ITEM.pago.boton}
                </Button>
              )}
            </Row>
            <p className="text-chico text-texto-suave">Seña y refuerzos: plata ya puesta que se descuenta el día de la escritura.</p>
            <ListaItems t={t} etiqueta="Pagos a cuenta" items={compra.pagos} vacio="Todavía no hay pagos a cuenta." conHonorarios={false} />
          </Stack>
        )}
        <Stack espacio={3}>
          <Row justificar="entre" envolver espacio={3}>
            <h3 className="font-semibold">Gastos de la compra</h3>
            {t.esAdmin && (
              <Button variante="secundario" tamanio="sm" onClick={() => t.abrirItem("previsto")}>
                {TEXTOS_ITEM.previsto.boton}
              </Button>
            )}
          </Row>
          <p className="text-chico text-texto-suave">
            Comisión inmobiliaria, escribanía, sellos: quedan previstos y no mueven plata hasta que confirmes la compra; ahí pasan a gastos pagados el día de la escritura. Total previsto: {formatMoneda(compra.previstos_usd, "USD")}.
          </p>
          <ListaPrevistos t={t} items={compra.previstos} />
          {compra.gastos.length > 0 && (
            <Stack espacio={2}>
              <p className="text-chico text-texto-suave">Ya pagados (cargados desde Gastos): {formatMoneda(compra.gastos_usd, "USD")}.</p>
              <ListaItems t={t} etiqueta="Gastos de la compra ya pagados" items={compra.gastos} vacio="" />
            </Stack>
          )}
        </Stack>
        {compra.honorarios_usd !== "0.00" && (
          <p className="text-chico text-texto-suave">Honorarios del fiduciario por la compra (aparte del costo): {formatMoneda(compra.honorarios_usd, "USD")}.</p>
        )}
        {compra.precio_acordado_usd !== null && <ParaConfirmar t={t} terreno={terreno} />}
      </Stack>
    </Card>
  );
}

// ------------------------------------------------------------------ compra confirmada

function ListaCompacta({ etiqueta, items, total, onBorrar }: { etiqueta: string; items: ItemCompra[]; total: string; onBorrar?: (i: ItemCompra) => void }) {
  return (
    <Stack espacio={2}>
      <h3 className="text-chico font-semibold text-texto-suave">{etiqueta}</h3>
      {items.length === 0 ? (
        <p className="text-chico text-texto-suave">Ninguno.</p>
      ) : (
        <ul aria-label={etiqueta} className="flex flex-col divide-y divide-borde">
          {items.map((i) => (
            <li key={i.gasto_id} className="flex items-center justify-between gap-3 py-2">
              <span className="min-w-0">
                <span className="block truncate">{conceptoItem(i)}</span>
                <span className="block text-chico text-texto-suave">{formatFecha(i.fecha)}</span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="text-right tabular-nums">{montoItem(i)}</span>
                {onBorrar && (
                  <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${i.concepto}`} onClick={() => onBorrar(i)}>
                    ✕
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="flex justify-between border-t border-borde-fuerte pt-2 text-chico font-semibold">
        <span>Total</span>
        <span className="tabular-nums">{formatMoneda(total, "USD")}</span>
      </p>
    </Stack>
  );
}

function ResumenCompra({ t, terreno, compra }: { t: Ficha; terreno: Terreno; compra: CompraTerreno }) {
  const escritura: Adjunto | undefined = terreno.adjuntos.find((a) => a.id === compra.escritura_adjunto_id);
  return (
    <Card role="region" aria-labelledby="titulo-compra">
      <Stack espacio={4}>
        <Row justificar="entre" alinear="centro" envolver espacio={3}>
          <Row espacio={2} alinear="centro">
            <TituloTarjeta id="titulo-compra">Compra</TituloTarjeta>
            <Badge tono="exito">Comprado</Badge>
          </Row>
          {t.esAdmin && (
            <Row espacio={1} envolver>
              <Button variante="fantasma" tamanio="sm" onClick={() => t.abrirItem("gasto")}>
                {TEXTOS_ITEM.gasto.boton}
              </Button>
              <Button variante="fantasma" tamanio="sm" onClick={t.pedirReabrir}>
                Reabrir compra
              </Button>
            </Row>
          )}
        </Row>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-control bg-superficie-2 p-3 sm:grid-cols-4">
          <Dato etiqueta="Fecha de escritura" valor={terreno.fecha_escritura ? formatFecha(terreno.fecha_escritura) : "Sin fecha"} />
          <Dato etiqueta="Precio acordado" valor={formatMoneda(compra.precio_acordado_usd ?? "0", "USD")} />
          <Dato etiqueta="Gastos de la compra" valor={formatMoneda(compra.gastos_usd, "USD")} />
          <Dato etiqueta="Costo total" valor={formatMoneda(compra.total_usd ?? "0", "USD")} />
        </dl>
        <Alerta texto={t.avisoArchivo} />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <ListaCompacta etiqueta="Pagos" items={compra.pagos} total={compra.pagado_usd} />
          <ListaCompacta etiqueta="Gastos de la compra" items={compra.gastos} total={compra.gastos_usd} onBorrar={t.esAdmin ? t.pedirBorrar : undefined} />
        </div>
        <div className="flex flex-col gap-1 border-t border-borde pt-3 text-chico text-texto-suave sm:flex-row sm:items-center sm:justify-between">
          <span>
            {compra.honorarios_usd !== "0.00" ? `Honorarios del fiduciario (aparte del costo): ${formatMoneda(compra.honorarios_usd, "USD")}` : "Sin honorarios del fiduciario"}
            {compra.confirmada_en && ` · Confirmada el ${formatFechaLocal(compra.confirmada_en)}`}
          </span>
          {escritura && (
            <a href={t.rutaDescarga(escritura)} download className="font-medium text-primario underline-offset-2 hover:underline">
              Descargar la escritura
            </a>
          )}
        </div>
      </Stack>
    </Card>
  );
}

function DialogoConfirmar({ t, terreno }: { t: Ficha; terreno: Terreno | null }) {
  const adjuntos = terreno?.adjuntos ?? [];
  return (
    <Dialog
      abierto={t.confirmarAbierto}
      onCambio={(abierto) => !abierto && t.cerrarConfirmar()}
      titulo="Confirmar compra"
      descripcion="Queda cerrada: para corregir precio, fecha o pagos habrá que reabrirla."
    >
      <form onSubmit={(e) => void t.confirmarCompra(e)} noValidate>
        <Stack espacio={4}>
          {terreno?.compra && textoPrevistosAlConfirmar(terreno.compra, terreno.fecha_escritura) && (
            <p className="rounded-control bg-superficie-2 p-3 text-chico">{textoPrevistosAlConfirmar(terreno.compra, terreno.fecha_escritura)}</p>
          )}
          {adjuntos.length > 0 ? (
            <Select
              etiqueta="Scan de la escritura"
              placeholder="Elegí un archivo del terreno"
              opciones={adjuntos.map((a) => ({ valor: String(a.id), etiqueta: a.nombre }))}
              valor={t.formConfirmar.escritura_adjunto_id === null ? undefined : String(t.formConfirmar.escritura_adjunto_id)}
              onCambio={(v) => t.elegirEscritura(Number(v))}
              error={t.erroresConfirmar.escritura_adjunto_id ?? null}
            />
          ) : (
            <Stack espacio={1}>
              <p className="text-chico text-texto-suave">Todavía no hay archivos del terreno: subí el scan de la escritura.</p>
              <Alerta texto={t.erroresConfirmar.escritura_adjunto_id ?? null} />
            </Stack>
          )}
          <Row espacio={2} alinear="centro" envolver>
            <SelectorArchivos etiqueta="Subir el scan de la escritura" textoBoton={adjuntos.length > 0 ? "Subir otro archivo" : "Subir el scan"} accept={ACCEPT_ADJUNTO} deshabilitado={t.subiendoEscritura} onElegir={([archivo]) => archivo && void t.subirEscritura(archivo)} />
            {t.subiendoEscritura && (
              <span className="text-primario">
                <Spinner tamanio="sm" etiqueta="Subiendo…" />
              </span>
            )}
          </Row>
          <Alerta texto={t.errorEscritura} />
          <Stack espacio={1}>
            <Casilla etiqueta="Confirmo que la compra se escrituró" marcado={t.formConfirmar.confirmo} onCambio={t.marcarConfirmo} />
            <Alerta texto={t.erroresConfirmar.confirmo ?? null} />
          </Stack>
          <Alerta texto={t.errorConfirmar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cerrarConfirmar}>
              Cancelar
            </Button>
            <Button type="submit" cargando={t.confirmando}>
              Confirmar
            </Button>
          </Row>
        </Stack>
      </form>
    </Dialog>
  );
}

// ------------------------------------------------------------------ pantalla

export function TerrenoTab() {
  const t = useTerrenoTab();
  const terreno = t.terreno;

  return (
    <Stack espacio={6}>
      <h1 className="text-pantalla font-light tracking-[-0.03em]">Terreno</h1>
      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Card>
          <Stack espacio={3}>
            <p>{t.errorCarga}</p>
            <div>
              <Button variante="secundario" onClick={t.reintentar}>
                Reintentar
              </Button>
            </div>
          </Stack>
        </Card>
      )}
      {terreno && (
        <>
          <TarjetaUbicacion t={t} terreno={terreno} />
          {terreno.compra &&
            (terreno.compra.confirmada ? <ResumenCompra t={t} terreno={terreno} compra={terreno.compra} /> : <CompraAbierta t={t} terreno={terreno} compra={terreno.compra} />)}
          <Adjuntos
            etiqueta="Documentación del terreno"
            adjuntos={terreno.adjuntos}
            rutaDescarga={t.rutaDescarga}
            puedeEditar={t.esAdmin}
            onSubir={t.subir}
            onBorrar={t.borrarAdjunto}
            subiendo={t.subiendo}
            error={t.errorAdjuntos}
          />
        </>
      )}

      <DialogoUbicacion t={t} />
      <DialogoConfirmar t={t} terreno={terreno} />

      <Dialog abierto={t.reabrirAbierto} onCambio={(abierto) => !abierto && t.cancelarReabrir()} titulo="¿Reabrir la compra?" descripcion="Vas a poder corregir precio, fecha y pagos. Después hay que volver a confirmarla.">
        <Stack espacio={4}>
          <Alerta texto={t.errorReabrir} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cancelarReabrir}>
              Cancelar
            </Button>
            <Button cargando={t.reabriendo} onClick={() => void t.reabrirCompra()}>
              Reabrir
            </Button>
          </Row>
        </Stack>
      </Dialog>

      <Dialog abierto={t.precioAbierto} onCambio={(abierto) => !abierto && t.cerrarPrecio()} titulo="Compra del terreno" descripcion="El precio acordado no mueve plata: la plata sale con cada pago a cuenta.">
        <form onSubmit={(e) => void t.guardarPrecio(e)} noValidate>
          <Stack espacio={4}>
            <TextField etiqueta="Precio acordado (USD)" inputMode="decimal" placeholder="Ej.: 420000" value={t.formPrecio.precio} onChange={(e) => t.cambiarPrecio("precio", e.target.value)} error={t.erroresPrecio.precio ?? null} />
            <TextField etiqueta="Fecha de escritura" type="date" ayuda="Opcional: cargala cuando se sepa" value={t.formPrecio.fecha_escritura} onChange={(e) => t.cambiarPrecio("fecha_escritura", e.target.value)} />
            <Alerta texto={t.errorPrecio} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrarPrecio}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardandoPrecio}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog
        abierto={t.tipoItem !== null}
        onCambio={(abierto) => !abierto && t.cerrarItem()}
        titulo={t.editandoPrevisto ? "Editar gasto previsto" : t.tipoItem ? TEXTOS_ITEM[t.tipoItem].titulo : ""}
        descripcion={t.tipoItem ? TEXTOS_ITEM[t.tipoItem].descripcion : ""}
      >
        {t.tipoItem && (
          <form onSubmit={(e) => void t.guardarItem(e)} noValidate>
            <Stack espacio={4}>
              <TextField etiqueta="Concepto" placeholder={TEXTOS_ITEM[t.tipoItem].ejemplo} value={t.formItem.concepto} onChange={(e) => t.cambiarItem("concepto", e.target.value)} error={t.erroresItem.concepto ?? null} />
              {t.tipoItem !== "previsto" && (
                <TextField etiqueta="Fecha" type="date" value={t.formItem.fecha} onChange={(e) => t.cambiarItem("fecha", e.target.value)} error={t.erroresItem.fecha ?? null} />
              )}
              <MontoField etiqueta="Monto" valor={t.formItem.monto} onCambio={(v) => t.cambiarItem("monto", v)} moneda={t.formItem.moneda} onCambioMoneda={(m) => t.cambiarItem("moneda", m)} error={t.erroresItem.monto} />
              {t.tipoItem === "pago" && t.hayQuienPuso && (t.formItem.pusieron.length < 2 ? (
                <Stack espacio={1}>
                  <Select etiqueta="Lo pagó" opciones={t.opcionesLoPago} valor={t.loPago} onCambio={t.elegirLoPago} error={t.erroresItem.pusieron ?? null} />
                  {t.formItem.pusieron.length === 1 && (
                    <p className="text-chico text-texto-suave">Se carga también como aporte suyo (el de un grupo, repartido por sus %), con la misma fecha y monto: queda a su favor en su cuenta.</p>
                  )}
                  <div>
                    <Button variante="fantasma" tamanio="sm" className="-ml-3" onClick={t.entreVarios}>
                      Lo pusieron entre varios
                    </Button>
                  </div>
                </Stack>
              ) : (
                <fieldset aria-label="Quiénes lo pusieron" className="rounded-control border border-borde p-3">
                  <Stack espacio={3}>
                    <span className="text-chico font-medium">Quiénes lo pusieron</span>
                    {t.formItem.pusieron.map((l, i) => (
                      <Row key={i} envolver espacio={2} alinear="fin">
                        <Select etiqueta={`Quién puso ${i + 1}`} opciones={t.opcionesPuso(i)} valor={l.quien || undefined} onCambio={(v) => t.cambiarPuso(i, "quien", v)} className="min-w-48 flex-1" />
                        <div className="w-44">
                          <MontoField etiqueta={`Cuánto puso ${i + 1}`} valor={l.monto} onCambio={(v) => t.cambiarPuso(i, "monto", v)} moneda={t.formItem.moneda} />
                        </div>
                        <Button variante="fantasma" tamanio="sm" aria-label={`Quitar a quien puso ${i + 1}`} onClick={() => t.quitarPuso(i)}>
                          Quitar
                        </Button>
                      </Row>
                    ))}
                    <div>
                      <Button variante="secundario" tamanio="sm" onClick={t.entreVarios}>
                        Agregar otro
                      </Button>
                    </div>
                    {t.erroresItem.pusieron ? (
                      <p className="text-chico text-peligro">{t.erroresItem.pusieron}</p>
                    ) : (
                      t.restoDelFideicomiso && <p className="text-chico text-texto-suave">El resto ({t.restoDelFideicomiso}) lo pone el fideicomiso.</p>
                    )}
                    <p className="text-chico text-texto-suave">Cada uno queda con un aporte por lo que puso (el de un grupo, repartido por sus %), con la misma fecha y en la misma caja.</p>
                  </Stack>
                </fieldset>
              ))}
              {t.formItem.moneda === "ARS" && (
                <TextField etiqueta="Tipo de cambio (ARS por USD)" inputMode="decimal" ayuda="Se sugiere el último usado" value={t.formItem.tipo_cambio} onChange={(e) => t.cambiarItem("tipo_cambio", e.target.value)} error={t.erroresItem.tipo_cambio ?? null} />
              )}
              {t.opcionesCaja && (t.formItem.partesCaja.length === 0 ? (
                <Stack espacio={1}>
                  <Select etiqueta="Caja" opciones={t.opcionesCaja} valor={t.formItem.caja_id} onCambio={(v) => t.cambiarItem("caja_id", v)} placeholder="Elegí la caja" />
                  <div>
                    <Button variante="fantasma" tamanio="sm" className="-ml-3" onClick={t.variasCajas}>
                      Salió de varias cajas
                    </Button>
                  </div>
                </Stack>
              ) : (
                <fieldset aria-label="De qué cajas salió" className="rounded-control border border-borde p-3">
                  <Stack espacio={3}>
                    <span className="text-chico font-medium">De qué cajas salió</span>
                    {t.formItem.partesCaja.map((p, i) => (
                      <Row key={i} envolver espacio={2} alinear="fin">
                        <Select etiqueta={`Caja ${i + 1}`} opciones={t.opcionesParteCaja(i)} valor={p.caja_id || undefined} onCambio={(v) => t.cambiarParteCaja(i, "caja_id", v)} className="min-w-48 flex-1" />
                        <div className="w-44">
                          <MontoField etiqueta={`Cuánto de la caja ${i + 1}`} valor={p.monto} onCambio={(v) => t.cambiarParteCaja(i, "monto", v)} moneda={t.formItem.moneda} />
                        </div>
                        <Button variante="fantasma" tamanio="sm" aria-label={`Quitar la caja ${i + 1}`} onClick={() => t.quitarParteCaja(i)}>
                          Quitar
                        </Button>
                      </Row>
                    ))}
                    <div>
                      <Button variante="secundario" tamanio="sm" onClick={t.variasCajas}>
                        Agregar otra caja
                      </Button>
                    </div>
                    {t.avisoCajas && <p className={t.erroresItem.cajas ? "text-chico text-peligro" : "text-chico text-texto-suave"}>{t.avisoCajas}</p>}
                  </Stack>
                </fieldset>
              ))}
              {t.tipoItem !== "pago" && (
                <Row espacio={2} alinear="centro" envolver>
                  <Button variante={t.formItem.lleva_fee ? "primario" : "secundario"} tamanio="sm" aria-pressed={t.formItem.lleva_fee} onClick={() => t.cambiarItem("lleva_fee", !t.formItem.lleva_fee)}>
                    {t.formItem.lleva_fee ? "Con honorarios del fiduciario" : "Sin honorarios del fiduciario"}
                  </Button>
                  {t.feePrevisto && t.feePorcentaje && <span className="text-chico text-texto-suave">{etiquetaFee(t.feePrevisto, t.feePorcentaje, t.formItem.moneda)}</span>}
                </Row>
              )}
              <Stack espacio={1}>
                <Row espacio={2} alinear="centro" envolver>
                  <SelectorArchivos etiqueta={`${TEXTOS_ITEM[t.tipoItem].archivo} (opcional)`} textoBoton={t.formItem.archivo ? "Cambiar archivo" : `Subir ${TEXTOS_ITEM[t.tipoItem].archivo.toLowerCase()}`} accept={ACCEPT_ADJUNTO} onElegir={([archivo]) => t.cambiarItem("archivo", archivo ?? null)} />
                  <span className="text-chico text-texto-suave">{t.formItem.archivo ? t.formItem.archivo.name : "Opcional"}</span>
                </Row>
                <Alerta texto={t.erroresItem.archivo ?? null} />
              </Stack>
              <Alerta texto={t.errorItem} />
              <Row justificar="fin" espacio={2}>
                <Button variante="secundario" onClick={t.cerrarItem}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.guardandoItem}>
                  Guardar
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>

      <Dialog
        abierto={t.aBorrar !== null}
        onCambio={(abierto) => !abierto && t.cancelarBorrar()}
        titulo={t.aBorrar ? `¿Borrar «${t.aBorrar.concepto}»?` : ""}
        descripcion={
          t.aBorrar?.pagado_por
            ? `Se borra también de Gastos y de la Caja, con sus honorarios, sus archivos y el aporte de ${t.aBorrar.pagado_por}.`
            : "Se borra también de Gastos y de la Caja, con sus honorarios y sus archivos."
        }
      >
        <Stack espacio={4}>
          <Alerta texto={t.errorBorrar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cancelarBorrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
              Borrar
            </Button>
          </Row>
        </Stack>
      </Dialog>

      <Dialog abierto={t.previstoABorrar !== null} onCambio={(abierto) => !abierto && t.cancelarBorrarPrevisto()} titulo={t.previstoABorrar ? `¿Borrar «${t.previstoABorrar.concepto}»?` : ""} descripcion="Se borra el gasto previsto con sus archivos.">
        <Stack espacio={4}>
          <Alerta texto={t.errorBorrarPrevisto} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cancelarBorrarPrevisto}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.borrandoPrevisto} onClick={() => void t.confirmarBorrarPrevisto()}>
              Borrar
            </Button>
          </Row>
        </Stack>
      </Dialog>
    </Stack>
  );
}
